'use client';

import { CLASSNAME, SHIPPING_SLA_TIER } from '@skin-seoul/service';
import type { FormattedShippingSLA } from '@skin-seoul/service';
import { Skeleton } from '@skin-seoul/design-system';

export interface ShippingSLASummaryProps {
  /** Formatted SLA payload from `formatShippingSLA` / `useShippingSLA` */
  formatted: FormattedShippingSLA;
  /** Additional wrapper classes */
  className?: string;
}

/**
 * Order-summary variant (SSD-3986 Phase 2) — Figma node 2589-35601.
 *
 * Condensed SLA surface used inside the checkout Order Summary card.
 * No icons, no pill, no confidence text — space is tight and the full block
 * already renders in the left column.
 *
 * Tier 1 (measured): two lines — "Expected" + "Latest".
 * Tier 2 (fallback): one line range — "Expected Delivery by {min} - {max}".
 */
export const ShippingSLASummary = ({ formatted, className }: ShippingSLASummaryProps) => {
  const { tier, averageDateText, latestDateText } = formatted;
  const isFallbackTier = tier === SHIPPING_SLA_TIER.Fallback;

  return (
    <div className={`flex flex-col gap-1 items-start ${CLASSNAME.NOT_TRANSLATE} ${className ?? ''}`}>
      {isFallbackTier ? (
        <div className="flex gap-1 items-center text-label-small text-neutral-700 whitespace-nowrap">
          <span className="font-regular">Expected Delivery by</span>
          <span className="font-medium">
            {averageDateText} - {latestDateText}
          </span>
        </div>
      ) : (
        <>
          <div className="flex gap-1 items-center text-label-small text-neutral-700 whitespace-nowrap">
            <span className="font-regular">Expected Delivery by</span>
            <span className="font-medium">{averageDateText}</span>
          </div>
          <div className="flex gap-1 items-center text-label-small text-neutral-700 whitespace-nowrap">
            <span className="font-regular">Latest Delivery by</span>
            <span className="font-medium">{latestDateText}</span>
          </div>
        </>
      )}
    </div>
  );
};

/** Loading skeleton matching the summary variant footprint. */
export const ShippingSLASummarySkeleton = ({ className }: { className?: string }) => (
  <div className={`flex flex-col gap-1 ${className ?? ''}`} aria-busy="true">
    <Skeleton className="h-4 w-40" />
    <Skeleton className="h-4 w-40" />
  </div>
);
